import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import {
  Table, Button, Card, Typography, Tag, Space, Row, Col, Descriptions,
  notification, Popconfirm, Tooltip, Spin, Modal
} from 'antd';
import { ArrowLeftOutlined, FallOutlined, StopOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';

import billingService from '../../../services/billingService';
import { formatMoney } from '../Orders/orderFormHelpers';
import { buildLineName, isAssorted, AssortmentBreakdown, linePriceLabel } from './invoiceLineHelpers';

// Loaded on demand: @react-pdf/renderer weighs several hundred KB and must stay
// out of the initial bundle.
const InvoicePdfPreview = lazy(() => import('./InvoicePdfDocument'));

const { Text, Title } = Typography;

const DISPATCH_UNIT_LABEL = { STEM: 'tallos', BUNCH: 'ramos', BOX: 'cajas' };
const BILLING_UNIT_LABEL = { TALLO: 'tallos', RAMO: 'ramos', CAJA: 'cajas' };

const formatCount = (value) => (Number(value) || 0).toLocaleString('es-CO');


const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingService.obtenerFactura(id);
      setInvoice(response?.data || null);
    } catch (error) {
      notification.error({ message: 'Error', description: 'No se pudo cargar la factura.' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleVoid = async () => {
    try {
      await billingService.anularFactura(invoice.invoice_id);
      notification.success({
        message: 'Factura anulada',
        description: `La factura ${invoice.display_number} quedó anulada y sus despachos vuelven a estar sin facturar.`,
      });
      await fetchInvoice();
    } catch (error) {
      notification.error({
        message: 'No se pudo anular la factura',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    }
  };

  const handlePrint = async () => {
    setPdfLoading(true);
    try {
      const response = await billingService.obtenerFacturaPdf(invoice.invoice_id);
      setPdfData(response?.data || null);
    } catch (error) {
      notification.error({
        message: 'No se pudo generar el PDF',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    } finally {
      setPdfLoading(false);
    }
  };

  if (!invoice) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        {loading ? <Spin /> : <Text type="secondary">Factura no encontrada.</Text>}
      </div>
    );
  }

  const isIssued = invoice.status === 'ISSUED';

  // One invoice can cover several dispatches, each from its own order.
  const orders = [...new Map(
    invoice.details.map((line) => [line.order_id, line.order_display_number])
  ).entries()];
  const dispatchNumbers = [...new Set(invoice.details.map((line) => line.dispatch_display_number))];

  const columns = [
    {
      title: 'Producto',
      key: 'product',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{buildLineName(record)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.product_sku}
            {record.mark_code ? ` · Marca ${record.mark_code}` : ''}
            {isAssorted(record) ? ' · Surtido' : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Orden',
      key: 'order',
      render: (_, record) => record.order_display_number || '-',
    },
    {
      title: 'Despachado',
      key: 'dispatched',
      align: 'right',
      render: (_, record) => `${formatCount(record.quantity)} ${DISPATCH_UNIT_LABEL[record.unit] || ''}`,
    },
    {
      title: 'Cobrado',
      key: 'invoiced',
      align: 'right',
      render: (_, record) => `${formatCount(record.invoiced)} ${BILLING_UNIT_LABEL[record.billing_unit] || ''}`,
    },
    {
      title: 'Precio',
      key: 'unit_price',
      align: 'right',
      render: (_, record) => linePriceLabel(record, invoice.currency),
    },
    {
      title: 'Valor',
      key: 'value',
      align: 'right',
      // El valor lo calcula el servidor: el congelado al despachar o, en
      // facturas viejas, cantidad × factor × precio.
      render: (_, record) => <Text strong>{formatMoney(record.amount, invoice.currency)}</Text>,
    },
    {
      title: 'Acreditado',
      key: 'credited',
      align: 'right',
      render: (_, record) =>
        Number(record.credited) > 0
          ? <Text type="danger">{formatCount(record.credited)} {BILLING_UNIT_LABEL[record.billing_unit] || ''}</Text>
          : <Text type="secondary">-</Text>,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
        <Col>
          <Space align="center">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/sales/billing?tab=facturas')} />
            <Title level={3} style={{ margin: 0, color: '#595959', fontWeight: 600 }}>
              Factura {invoice.display_number}
            </Title>
            {isIssued ? (
              <Tag color="success">Emitida</Tag>
            ) : (
              <Tooltip
                title={`Anulada${invoice.voider ? ` por ${invoice.voider.full_name}` : ''}${
                  invoice.voided_at ? ` el ${dayjs(invoice.voided_at).format('DD/MM/YYYY HH:mm')}` : ''}`}
              >
                <Tag color="error">Anulada</Tag>
              </Tooltip>
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<PrinterOutlined />} loading={pdfLoading} onClick={handlePrint}>
              Imprimir
            </Button>
            {isIssued && (
              <>
                <Button
                  icon={<FallOutlined />}
                  onClick={() => navigate(`/sales/credit-notes?invoice_id=${invoice.invoice_id}`)}
                  style={{ borderColor: '#d9d9d9', color: '#8c8c8c' }}
                >
                  Registrar baja
                </Button>
                <Popconfirm
                  title="Anular factura"
                  description={`¿Anular la factura ${invoice.display_number}? Sus despachos quedarán sin facturar.`}
                  okText="Anular"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                  onConfirm={handleVoid}
                >
                  <Button danger icon={<StopOutlined />}>Anular</Button>
                </Popconfirm>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
          <Descriptions.Item label="Cliente">{invoice.client?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Fecha">
            {invoice.date ? dayjs(invoice.date.slice(0, 10)).format('DD/MM/YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Moneda">{invoice.currency}</Descriptions.Item>
          <Descriptions.Item label={orders.length > 1 ? 'Órdenes' : 'Orden'}>
            <Space size={4} wrap>
              {orders.map(([orderId, orderNumber]) => (
                <Link key={orderId} to={`/sales/orders/${orderId}`}>{orderNumber}</Link>
              ))}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={dispatchNumbers.length > 1 ? 'Despachos' : 'Despacho'}>
            {dispatchNumbers.join(', ')}
          </Descriptions.Item>
          <Descriptions.Item label="Fito">{(invoice.fitos || []).join(', ')}</Descriptions.Item>
          <Descriptions.Item label="Total">{formatMoney(invoice.total, invoice.currency)}</Descriptions.Item>
          <Descriptions.Item label="Bajas">
            {Number(invoice.credited) > 0
              ? <Text type="danger">- {formatMoney(invoice.credited, invoice.currency)}</Text>
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Saldo">
            <Text strong>{formatMoney(invoice.balance, invoice.currency)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Table
        columns={columns}
        dataSource={invoice.details}
        rowKey={(record) => record.invoice_detail_id}
        loading={loading}
        scroll={{ x: 1000 }}
        pagination={false}
        expandable={{
          rowExpandable: isAssorted,
          expandedRowRender: (record) => <AssortmentBreakdown components={record.components} boxes={record.quantity} />,
        }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={6} align="right">
              <Text strong>Total</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              <Text strong>{formatMoney(invoice.total, invoice.currency)}</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} />
          </Table.Summary.Row>
        )}
      />

      <Modal
        open={Boolean(pdfData)}
        onCancel={() => setPdfData(null)}
        footer={null}
        width={900}
        destroyOnHidden
        title={`Factura ${invoice.display_number}`}
      >
        {pdfData && (
          <Suspense fallback={<div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>}>
            <InvoicePdfPreview data={pdfData} />
          </Suspense>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceDetailPage;
